import { chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';

export type SeriesType = 'BILL' | 'SUBSCRIPTION' | 'TRANSFER' | 'INCOME';

type AccountExcludeDefaults = {
  excludeFromSpendingPlan: boolean;
  excludeFromReports: boolean;
};

type TxnLike = {
  coa?: ChartOfAccountProps | null;
  amount?: number | '' | null;
};

export async function applySeriesTypeChange(args: {
  nextType: SeriesType;
  create: boolean;
  currentTxn: TxnLike;
  incomeCategoryId?: string | null;
  accountExcludeDefaults: AccountExcludeDefaults;
  setFieldValue: (path: string, value: unknown) => unknown;
}): Promise<void> {
  const { nextType, create, currentTxn, incomeCategoryId, accountExcludeDefaults, setFieldValue } = args;

  const currentCoa = currentTxn?.coa ?? null;
  const rawAmount = currentTxn?.amount ?? 0;
  const currentAmount = typeof rawAmount === 'number' ? rawAmount : Number(rawAmount) || 0;

  const coaIsTransfer = Boolean(currentCoa && chartOfAccountsUtils.isTransferCOA(currentCoa));
  const coaIsIncomeCategory = Boolean(
    currentCoa?.type === 'CATEGORY' && categoriesSelectors.isIncomeCat(null, currentCoa.id),
  );

  // Reset exclude flags to defaults whenever the Type changes.
  // - Non-transfer types: default to the account's exclusion settings and clear dest-side flags.
  // - Transfer: default exclude both sides for Spending Plan + Reports.
  if (nextType === 'TRANSFER') {
    setFieldValue('transaction.isExcludedFromF2S', true);
    setFieldValue('transaction.excludeF2SDest', true);
    setFieldValue('transaction.isExcludedFromReports', true);
    setFieldValue('transaction.excludeReportsDest', true);
  } else {
    setFieldValue('transaction.isExcludedFromF2S', accountExcludeDefaults.excludeFromSpendingPlan);
    setFieldValue('transaction.excludeF2SDest', false);
    setFieldValue('transaction.isExcludedFromReports', accountExcludeDefaults.excludeFromReports);
    setFieldValue('transaction.excludeReportsDest', false);
  }

  if (nextType === 'BILL' || nextType === 'SUBSCRIPTION') {
    setFieldValue('transaction.isTransfer', false);
    setFieldValue('transaction.isIncome', false);
    setFieldValue('transaction.isBill', nextType === 'BILL');
    setFieldValue('transaction.isSubscription', nextType === 'SUBSCRIPTION');

    // If we were previously a Transfer/Income, clear the COA to avoid being re-classified on submit.
    if (coaIsTransfer || coaIsIncomeCategory) {
      await Promise.resolve(setFieldValue('transaction.coa', { type: 'UNCATEGORIZED', id: '0' }));
    }

    // If we were previously an income (positive amount) and user chooses Bill/Subscription on create, flip sign back.
    if (create && currentAmount > 0 && coaIsIncomeCategory) {
      setFieldValue('transaction.amount', Number(currentAmount * -1));
    }
    return;
  }

  if (nextType === 'TRANSFER') {
    setFieldValue('transaction.isBill', false);
    setFieldValue('transaction.isSubscription', false);
    setFieldValue('transaction.isIncome', false);
    setFieldValue('transaction.isTransfer', true);

    if (!coaIsTransfer) {
      await Promise.resolve(setFieldValue('transaction.coa', { type: 'BALANCE_ADJUSTMENT', id: '2' }));
    }
    return;
  }

  // INCOME
  setFieldValue('transaction.isBill', false);
  setFieldValue('transaction.isSubscription', false);
  setFieldValue('transaction.isTransfer', false);
  setFieldValue('transaction.isIncome', true);
  if (incomeCategoryId) {
    await Promise.resolve(setFieldValue('transaction.coa', { type: 'CATEGORY', id: incomeCategoryId }));
  }
  if (create && currentAmount < 0) {
    setFieldValue('transaction.amount', Math.abs(Number(currentAmount)));
  }
}
